import { Alert, Button, Card, Descriptions, Form, Input, InputNumber, Modal, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import EducationPageChrome from './components/EducationPageChrome';

type Scheme = {
  id: string; name: string; status: string; publish_hash?: string; published_at?: string;
  moderation_required?: number | boolean; moderated_at?: string | null; moderation_note?: string | null;
};
type Entry = {
  id: string; admission_no: string; first_name: string; last_name?: string; component_name: string;
  marks_raw: number; maximum_marks: number; status: string; correction_reason?: string;
};

export default function AssessmentPublishPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [moderationNote, setModerationNote] = useState('');
  const scheme = useQuery({
    queryKey: ['education-assessment-scheme', id],
    queryFn: async () => (await api.get<Scheme>(`/education/assessments/schemes/${id}`)).data,
  });
  const entries = useQuery({
    queryKey: ['education-assessment-entries', id],
    queryFn: async () => (await api.get<Entry[]>(`/education/assessments/schemes/${id}/entries`)).data || [],
  });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['education-assessment-scheme', id] });
    queryClient.invalidateQueries({ queryKey: ['education-assessment-entries', id] });
    queryClient.invalidateQueries({ queryKey: ['education-assessment-schemes'] });
  };
  const publish = useMutation({
    mutationFn: () => api.post(`/education/assessments/schemes/${id}/publish`, {}),
    onSuccess: () => { message.success(t('assessmentPublished')); refresh(); },
    onError: (error: { response?: { data?: { message?: string } } }) => message.error(error.response?.data?.message || t('unavailable')),
  });
  const moderate = useMutation({
    mutationFn: () => api.post(`/education/assessments/schemes/${id}/moderate`, { moderation_note: moderationNote.trim() || undefined }),
    onSuccess: () => { message.success(t('assessmentModerated')); refresh(); },
    onError: (error: { response?: { data?: { message?: string } } }) => message.error(error.response?.data?.message || t('unavailable')),
  });
  const correct = useMutation({
    mutationFn: ({ entryId, values }: { entryId: string; values: Record<string, unknown> }) =>
      api.post(`/education/assessments/entries/${entryId}/correct`, values),
    onSuccess: () => { message.success(t('assessmentCorrected')); Modal.destroyAll(); refresh(); },
    onError: (error: { response?: { data?: { message?: string } } }) => message.error(error.response?.data?.message || t('unavailable')),
  });
  const openCorrection = (entry: Entry) => {
    form.setFieldsValue({ marks_raw: entry.marks_raw, correction_reason: '' });
    Modal.confirm({
      title: t('correctPublishedEntry'),
      icon: null,
      content: (
        <Form form={form} layout="vertical">
          <Form.Item name="marks_raw" label={t('marksRaw')} rules={[{ required: true }]}>
            <InputNumber min={0} max={entry.maximum_marks} />
          </Form.Item>
          <Form.Item name="correction_reason" label={t('correctionReason')} rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      ),
      onOk: async () => correct.mutateAsync({ entryId: entry.id, values: await form.validateFields() }),
    });
  };
  const openEntries = (entries.data || []).filter((e) => e.status === 'draft' || e.status === 'locked');
  const allLocked = openEntries.length > 0 && openEntries.every((e) => e.status === 'locked');
  const moderationRequired = Number(scheme.data?.moderation_required) === 1 || scheme.data?.moderation_required === true;
  const moderationBlocksPublish = moderationRequired && !scheme.data?.moderated_at;
  return (
    <EducationPageChrome
      title={t('assessmentPublish')}
      description={scheme.data?.name}
      extra={<Button onClick={() => navigate('/education/assessments')}>{t('back')}</Button>}
    >
      <Alert type="warning" showIcon message={t('assessmentHumanPublishNotice')} style={{ marginBottom: 16 }} />
      <Card size="small" loading={scheme.isLoading}>
        <Descriptions items={[
          { key: 'status', label: t('status'), children: <Tag>{scheme.data?.status}</Tag> },
          { key: 'hash', label: t('publishHash'), children: scheme.data?.publish_hash || '—' },
          { key: 'published', label: t('publishedAt'), children: scheme.data?.published_at || '—' },
          { key: 'moderation', label: t('assessmentModeration'), children: scheme.data?.moderated_at
            ? `${scheme.data.moderated_at}${scheme.data.moderation_note ? ` — ${scheme.data.moderation_note}` : ''}`
            : (moderationRequired ? t('assessmentModerationRequired') : '—') },
        ]} />
        {scheme.data?.status !== 'published' && allLocked ? (
          <div style={{ margin: '12px 0' }}>
            <Input.TextArea
              rows={2}
              maxLength={1000}
              value={moderationNote}
              onChange={(e) => setModerationNote(e.target.value)}
              placeholder={t('assessmentModerationNote')}
              style={{ marginBottom: 8 }}
            />
            <Button loading={moderate.isPending} onClick={() => moderate.mutate()}>{t('assessmentModerate')}</Button>
          </div>
        ) : null}
        {scheme.data?.status !== 'published' ? (
          <Button type="primary" danger disabled={moderationBlocksPublish} loading={publish.isPending} onClick={() => Modal.confirm({
            title: t('confirmAssessmentPublish'),
            content: t('assessmentPublishConfirmNotice'),
            onOk: () => publish.mutateAsync(),
          })}>{t('assessmentPublish')}</Button>
        ) : null}
      </Card>
      <Card size="small" style={{ marginTop: 16 }}>
        <Table rowKey="id" loading={entries.isLoading} dataSource={entries.data || []} columns={[
          { title: t('student'), render: (_, row: Entry) => `${row.admission_no} — ${row.first_name} ${row.last_name || ''}` },
          { title: t('component'), dataIndex: 'component_name' },
          { title: t('marksRaw'), dataIndex: 'marks_raw' },
          { title: t('status'), dataIndex: 'status', render: (v: string) => <Tag>{v}</Tag> },
          { title: t('actions'), render: (_, row: Entry) => row.status === 'published' ? <Button size="small" onClick={() => openCorrection(row)}>{t('correct')}</Button> : null },
        ]} />
      </Card>
    </EducationPageChrome>
  );
}
